<!--
  DecorationProcessDpiThresholds -- SuperAdmin DPI-threshold editor
  for decoration_processes.

  Decoration Model Refactor -- Phase P2 (PR-D).
  Card: Boulders #163 (https://trello.com/c/S5kN5poU)

  Why this page exists
  ====================
  Locked product decision #2 (DPI thresholds): runtime-editable
  without a deploy. P1 PR-F added the column + seed; this page is the
  ops surface that lets a SuperAdmin tune the per-method threshold
  post-beta from customer feedback.

  IA
  ==
  One row per decoration_process. Each row carries:

    * slug (e.g. screen_print)
    * display name (e.g. Screen Print)
    * editable integer input for dpi_enlarge_threshold (the RECOMMENDED DPI:
      below it a raster gets an amber "may print soft" note)
    * "Clear" button to set the threshold to NULL
    * editable integer input for dpi_minimum (card #573, Jake 2026-10-28:
      below it a raster is refused or flagged red, per the action) with its
      own "Clear" (NULL = no minimum)
    * a Reject / Warn toggle for dpi_below_minimum_action: Warn (the default
      since Jake 2026-10-29) allows placing or resizing below the minimum,
      flagged; Reject refuses it

  Saving is per-row (auto-save on blur / on Clear). Inactive
  processes are surfaced but grayed; their threshold is still
  editable in case a soft-retired method is re-activated later.

  Endpoints
  =========
  * GET   /api/admin/decoration-processes
  * PATCH /api/admin/decoration-processes/<id>/dpi-threshold
  * PATCH /api/admin/decoration-processes/<id>/dpi-minimum
    ({dpi_minimum} and/or {dpi_below_minimum_action}; each change audited)

  Mounted at /admin/decoration-processes/dpi-thresholds via
  router/groups/superadmin.js. Gated on superadmin.settings.

  data-testid prefix: dpi-thresholds-*
-->
<template>
    <section
        class="dpi-thresholds"
        data-testid="dpi-thresholds-root"
    >
        <header class="dpi-thresholds__header">
            <h2 class="dpi-thresholds__title">
                Artwork resolution (DPI)
            </h2>
        </header>

        <p class="dpi-thresholds__lede">
            Per decoration method. <strong>Recommended DPI</strong>: a raster
            image below it at the size a distributor chose gets an amber
            note and is still allowed. <strong>Minimum DPI</strong>: below
            it the image is too small to print well. <strong>Warn</strong> (the
            default) allows it, flagged in red, and the distributor may opt
            into an art cleanup; <strong>Reject</strong>
            refuses placing or resizing to that size, including when a saved
            set is applied, where that placement is skipped and the rest still
            apply (the distributor sees the largest size that works). Empty means no check. Vector files are never
            checked. Existing placements are never changed by these
            settings; they only show the icon.
        </p>

        <div
            v-if="loading"
            data-testid="dpi-thresholds-loading"
        >
            Loading...
        </div>

        <div
            v-else-if="!processes.length"
            class="dpi-thresholds__empty"
            data-testid="dpi-thresholds-empty"
        >
            No decoration processes defined.
        </div>

        <table
            v-else
            class="dpi-thresholds__table"
            data-testid="dpi-thresholds-table"
        >
            <thead>
                <tr>
                    <th>Slug</th>
                    <th>Display name</th>
                    <th>Status</th>
                    <th>Recommended DPI</th>
                    <th>Minimum DPI</th>
                    <th>Below the minimum</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="proc in processes"
                    :key="proc.id"
                    :class="{
                        'dpi-thresholds__row--inactive': !proc.is_active,
                    }"
                    :data-testid="`dpi-thresholds-row-${proc.id}`"
                >
                    <td>{{ proc.slug }}</td>
                    <td>{{ proc.display_name }}</td>
                    <td>
                        <span v-if="proc.is_active">Active</span>
                        <span
                            v-else
                            class="dpi-thresholds__inactive-badge"
                        >
                            Inactive
                        </span>
                    </td>
                    <td>
                        <input
                            type="number"
                            min="50"
                            max="1200"
                            :value="drafts[proc.id]"
                            :disabled="saving"
                            :data-testid="`dpi-thresholds-input-${proc.id}`"
                            @input="
                                drafts[proc.id] = $event.target.value
                            "
                            @blur="onSave(proc.id)"
                            @keydown.enter="onSave(proc.id)"
                        />
                        <span
                            v-if="drafts[proc.id] === '' || drafts[proc.id] === null"
                            class="dpi-thresholds__null-badge"
                            :data-testid="`dpi-thresholds-null-${proc.id}`"
                        >
                            no gate
                        </span>
                    </td>
                    <td>
                        <input
                            type="number"
                            min="50"
                            max="1200"
                            :value="minDrafts[proc.id]"
                            :disabled="saving"
                            :data-testid="`dpi-thresholds-min-input-${proc.id}`"
                            @input="
                                minDrafts[proc.id] = $event.target.value
                            "
                            @blur="onSaveMinimum(proc.id)"
                            @keydown.enter="onSaveMinimum(proc.id)"
                        />
                        <span
                            v-if="minDrafts[proc.id] === '' || minDrafts[proc.id] === null"
                            class="dpi-thresholds__null-badge"
                            :data-testid="`dpi-thresholds-min-null-${proc.id}`"
                        >
                            no minimum
                        </span>
                    </td>
                    <td>
                        <div
                            class="dpi-thresholds__toggle"
                            role="group"
                            aria-label="Action below the minimum DPI"
                        >
                            <button
                                v-for="choice in ACTIONS"
                                :key="choice.value"
                                type="button"
                                class="dpi-thresholds__toggle-btn"
                                :class="{
                                    'dpi-thresholds__toggle-btn--on':
                                        actionOf(proc) === choice.value,
                                }"
                                :aria-pressed="String(actionOf(proc) === choice.value)"
                                :disabled="saving"
                                :title="choice.hint"
                                :data-testid="`dpi-thresholds-action-${choice.value}-${proc.id}`"
                                @click="onSetAction(proc.id, choice.value)"
                            >
                                {{ choice.label }}
                            </button>
                        </div>
                    </td>
                    <td>
                        <button
                            type="button"
                            class="tf-btn tf-btn--ghost tf-btn--sm"
                            :disabled="saving"
                            :data-testid="`dpi-thresholds-clear-${proc.id}`"
                            @click="onClear(proc.id)"
                        >
                            Clear recommended
                        </button>
                        <button
                            type="button"
                            class="tf-btn tf-btn--ghost tf-btn--sm"
                            :disabled="saving"
                            :data-testid="`dpi-thresholds-min-clear-${proc.id}`"
                            @click="onClearMinimum(proc.id)"
                        >
                            Clear minimum
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>

        <p
            v-if="successMsg"
            class="dpi-thresholds__success"
            data-testid="dpi-thresholds-success"
        >
            {{ successMsg }}
        </p>

        <p
            v-if="errorMsg"
            class="dpi-thresholds__error"
            data-testid="dpi-thresholds-error"
        >
            {{ errorMsg }}
        </p>
    </section>
</template>

<script>
import axios from "@/axios";

export default {
    name: "DecorationProcessDpiThresholds",
    data() {
        return {
            processes: [],
            // Per-id draft string for the editable input. Strings
            // (not numbers) so the empty-string -> NULL path is
            // unambiguous.
            drafts: {},
            // The same for the minimum DPI.
            minDrafts: {},
            ACTIONS: [
                {
                    value: "reject",
                    label: "Reject",
                    hint: "Refuse placing or resizing below the minimum",
                },
                {
                    value: "warn",
                    label: "Warn",
                    hint: "Allow it, flagged in red (the default)",
                },
            ],
            loading: false,
            saving: false,
            successMsg: "",
            errorMsg: "",
        };
    },
    mounted() {
        this.fetch();
    },
    methods: {
        async fetch() {
            this.loading = true;
            this.errorMsg = "";
            this.successMsg = "";
            try {
                const resp = await axios.get(
                    "/api/admin/decoration-processes"
                );
                this.processes = resp.data.processes || [];
                this.drafts = {};
                this.minDrafts = {};
                for (const p of this.processes) {
                    this.drafts[p.id] =
                        p.dpi_enlarge_threshold === null
                            ? ""
                            : String(p.dpi_enlarge_threshold);
                    this.minDrafts[p.id] = this._minimumText(p);
                }
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error ||
                    "Failed to load processes.";
            } finally {
                this.loading = false;
            }
        },
        _minimumText(proc) {
            return proc.dpi_minimum === null || proc.dpi_minimum === undefined
                ? ""
                : String(proc.dpi_minimum);
        },
        actionOf(proc) {
            // Warn is the default (Jake, 2026-10-29): nothing is blocked out of the box.
            return proc.dpi_below_minimum_action || "warn";
        },
        _normalizeDraft(raw) {
            // Returns null for empty / unparseable; integer otherwise.
            if (raw === "" || raw === null || raw === undefined) {
                return null;
            }
            const n = parseInt(raw, 10);
            if (Number.isNaN(n)) return null;
            return n;
        },
        _currentValueFor(processId) {
            const p = this.processes.find((x) => x.id === processId);
            return p ? p.dpi_enlarge_threshold : null;
        },
        async _patch(processId, value) {
            this.saving = true;
            this.errorMsg = "";
            this.successMsg = "";
            try {
                const resp = await axios.patch(
                    `/api/admin/decoration-processes/${processId}/dpi-threshold`,
                    { dpi_enlarge_threshold: value }
                );
                // Reflect the canonical value back in the list +
                // drafts so the row settles to whatever the backend
                // returns (handles round-tripping NULL correctly).
                const idx = this.processes.findIndex(
                    (x) => x.id === processId
                );
                if (idx !== -1) {
                    this.processes[idx] = resp.data;
                }
                this.drafts[processId] =
                    resp.data.dpi_enlarge_threshold === null
                        ? ""
                        : String(resp.data.dpi_enlarge_threshold);
                this.successMsg = `Saved (${resp.data.slug}).`;
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error || "Save failed.";
                // Revert the draft on failure so the input doesn't
                // strand a bogus value in the UI.
                this.drafts[processId] =
                    this._currentValueFor(processId) === null
                        ? ""
                        : String(this._currentValueFor(processId));
            } finally {
                this.saving = false;
            }
        },
        async onSave(processId) {
            const draft = this._normalizeDraft(this.drafts[processId]);
            const current = this._currentValueFor(processId);
            // No-op when the draft matches the canonical value (avoids
            // a save round-trip on every onBlur tab-out).
            if (draft === current) return;
            await this._patch(processId, draft);
        },
        async onClear(processId) {
            // Explicit clear -> NULL. Sets the draft first so the
            // UI shows the empty state immediately.
            this.drafts[processId] = "";
            await this._patch(processId, null);
        },
        // ---- Minimum DPI and the action below it (card #573) ----
        async _patchMinimum(processId, body) {
            this.saving = true;
            this.errorMsg = "";
            this.successMsg = "";
            try {
                const resp = await axios.patch(
                    `/api/admin/decoration-processes/${processId}/dpi-minimum`,
                    body
                );
                const idx = this.processes.findIndex(
                    (x) => x.id === processId
                );
                if (idx !== -1) {
                    this.processes[idx] = resp.data;
                }
                this.minDrafts[processId] = this._minimumText(resp.data);
                this.successMsg = `Saved (${resp.data.slug}).`;
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error || "Save failed.";
                // Put the input back on the saved value.
                const p = this.processes.find((x) => x.id === processId);
                this.minDrafts[processId] = p ? this._minimumText(p) : "";
            } finally {
                this.saving = false;
            }
        },
        async onSaveMinimum(processId) {
            const draft = this._normalizeDraft(this.minDrafts[processId]);
            const p = this.processes.find((x) => x.id === processId);
            const current =
                p && p.dpi_minimum !== undefined ? p.dpi_minimum : null;
            if (draft === current) return;
            await this._patchMinimum(processId, { dpi_minimum: draft });
        },
        async onClearMinimum(processId) {
            this.minDrafts[processId] = "";
            await this._patchMinimum(processId, { dpi_minimum: null });
        },
        async onSetAction(processId, action) {
            const p = this.processes.find((x) => x.id === processId);
            if (p && this.actionOf(p) === action) return;
            await this._patchMinimum(processId, {
                dpi_below_minimum_action: action,
            });
        },
    },
};
</script>

<style scoped>
.dpi-thresholds {
    padding: 1.5rem;
}
.dpi-thresholds__header {
    margin-bottom: 0.5rem;
}
.dpi-thresholds__title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
}
.dpi-thresholds__lede {
    color: #6b7280;
    max-width: 720px;
    margin-bottom: 1rem;
}
.dpi-thresholds__table {
    width: 100%;
    border-collapse: collapse;
}
.dpi-thresholds__table th,
.dpi-thresholds__table td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    font-size: 0.875rem;
}
.dpi-thresholds__row--inactive {
    color: var(--tf-fg-quiet);
}
.dpi-thresholds__inactive-badge {
    padding: 0.125rem 0.5rem;
    background: #f3f4f6;
    color: #6b7280;
    border-radius: 999px;
    font-size: 0.75rem;
}
.dpi-thresholds__toggle {
    display: inline-flex;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    overflow: hidden;
}
.dpi-thresholds__toggle-btn {
    padding: 0.25rem 0.75rem;
    background: #fff;
    border: 0;
    font-size: 0.8125rem;
    cursor: pointer;
}
.dpi-thresholds__toggle-btn + .dpi-thresholds__toggle-btn {
    border-left: 1px solid #d1d5db;
}
.dpi-thresholds__toggle-btn--on {
    background: #119ae9;
    color: #fff;
}
.dpi-thresholds__null-badge {
    margin-left: 0.5rem;
    padding: 0.125rem 0.5rem;
    background: #fef3c7;
    color: #92400e;
    border-radius: 999px;
    font-size: 0.75rem;
}
.dpi-thresholds__empty {
    padding: 2rem;
    text-align: center;
    color: var(--tf-fg-4);
}
.dpi-thresholds__success {
    color: #047857;
    margin-top: 0.75rem;
}
.dpi-thresholds__error {
    color: #dc2626;
    margin-top: 0.75rem;
}
</style>
